import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Fetch user's permissions from their IAM role
      if (response.data.role_id) {
        try {
          const roleRes = await axios.get(`${API}/roles/${response.data.role_id}`);
          setPermissions(roleRes.data.permissions || []);
        } catch (err) {
          console.error('Failed to fetch role permissions:', err);
          setPermissions([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    // Fetch permissions after login
    if (userData.role_id) {
      try {
        const roleRes = await axios.get(`${API}/roles/${userData.role_id}`);
        setPermissions(roleRes.data.permissions || []);
      } catch (err) {
        console.error('Failed to fetch role permissions:', err);
      }
    }
    return userData;
  };

  const register = async (name, email, password, role = 'creator') => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setPermissions([]);
  };

  const hasRole = (...roles) => {
    return user && roles.includes(user.role);
  };

  // Check if user has a specific permission
  const hasPermission = (...requiredPermissions) => {
    // Admin has all permissions
    if (permissions.includes('admin.full_access')) return true;
    // Check if user has any of the required permissions
    return requiredPermissions.some(perm => permissions.includes(perm));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, hasRole, hasPermission, permissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
